import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { DocumentDate } from '@features/invoices/elements/DocumentDate'
import { DocumentDetailsCard } from '@features/invoices/elements/DocumentDetailsCard'

import { useInvoice } from '../../../../contexts/invoiceContext'
import { useDisplayInvoiceDetailsCard } from '../../../../hooks/useDisplayInvoiceDetailsCard'
import { InvoiceRecurrencePageLink } from './elements/InvoiceRecurrencePageLink'
import { InvoiceRelatedDocuments } from './elements/InvoiceRelatedDocuments'
import { InvoiceTransactionsList } from './elements/InvoiceTransactionsList'

export const InvoiceDetailsCard = (): ReactElement | null => {
  const { t } = useTranslation()
  const { invoice } = useInvoice()

  const {
    isLoading,
    shouldDisplayCustomerLink,
    shouldDisplayInvoiceDetailsCard,
    shouldDisplayIssueDate,
    shouldDisplayRecurringReference,
    shouldDisplayRelatedDocumentLinks,
    shouldDisplayTransactionLink,
  } = useDisplayInvoiceDetailsCard()

  if (!invoice || !shouldDisplayInvoiceDetailsCard) {
    return null
  }

  return (
    <DocumentDetailsCard isLoading={isLoading}>
      {shouldDisplayRelatedDocumentLinks && (
        <DocumentDetailsCard.Entry title={t('external_invoices.view.sidebar.details_card.related_to')}>
          <InvoiceRelatedDocuments />
        </DocumentDetailsCard.Entry>
      )}

      {shouldDisplayCustomerLink && (
        <DocumentDetailsCard.Entry title={t('external_invoices.view.sidebar.details_card.customer')}>
          <DocumentDetailsCard.CustomerLink
            displayName={invoice?.customer?.displayName}
            externalContactId={invoice?.customer?.externalContactId}
          />
        </DocumentDetailsCard.Entry>
      )}

      {shouldDisplayIssueDate && (
        <DocumentDetailsCard.Entry title={t('external_invoices.view.sidebar.details_card.issue_date')}>
          <DocumentDate date={invoice.issueDate} />
        </DocumentDetailsCard.Entry>
      )}

      {shouldDisplayTransactionLink && (
        <DocumentDetailsCard.Entry title={t('external_invoices.view.sidebar.details_card.transaction_number')}>
          <InvoiceTransactionsList />
        </DocumentDetailsCard.Entry>
      )}

      {shouldDisplayRecurringReference && (
        <DocumentDetailsCard.Entry title={t('external_invoices.view.sidebar.details_card.part_of_recurrence')}>
          <InvoiceRecurrencePageLink recurringInvoiceId={invoice?.recurringProfileId} />
        </DocumentDetailsCard.Entry>
      )}
    </DocumentDetailsCard>
  )
}
